/**
 * Diaper Search — filter / sort pipeline session_samples and open Inspector
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useClipDataStore } from '../stores/clipDataStore';
import { useSearchStore } from '../stores/searchStore';
import { useDiaperStore } from '../stores/diaperStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { getAllClipMeta, BackendMeta, AnalysisSession, CLIP_MONITOR_IS_REMOTE } from '../lib/api-clip-monitor';
import {
  BOOLEAN_SESSION_FIELDS,
  NUMERIC_SESSION_FIELDS,
  STRING_SESSION_FIELDS,
  OBJECT_SESSION_FIELDS,
  SORTABLE_FIELDS,
  matchesBooleanFilter,
  matchesNumericFilter,
  compareSessionField,
  formatCell,
  formatJsonPreview,
} from '../lib/sessionSampleFields';
import type { TriState } from '../lib/sessionSampleFields';

type SessionRow = AnalysisSession;

function formatDurationHours(h: number | undefined): string {
  if (h === undefined || Number.isNaN(h)) return '—';
  const m = Math.round(h * 60);
  if (m < 60) return `${m}m`;
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${hh}h ${mm}m` : `${hh}h`;
}

const BOOL_HEADER_SHORT: Record<string, string> = {
  flatline_like: 'flat',
  mostly_dry: 'dry',
  quickly_high_wetness: 'qWet',
  left_truncated: 'Ltr',
  right_truncated: 'Rtr',
  session_complete: 'done',
  baseline_trustworthy: 'base',
  backend_matched: 'back',
  is_day_start: 'day',
};

export const DiaperSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    serverOnline,
    checkServer,
    activeBucket,
    availableBuckets,
    loadingBuckets,
    loadBuckets,
    switchBucket,
  } = useClipDataStore();

  const {
    sessions,
    loading,
    error,
    resultsPath,
    analysisBaseDir,
    loadedBucket,
    modelFilter,
    siteFilter,
    textSearch,
    textCriterion,
    booleanFilters,
    numericRange,
    sortField,
    sortDesc,
    setModelFilter,
    setSiteFilter,
    setTextSearch,
    setTextCriterion,
    setBooleanFilter,
    setNumericMin,
    setNumericMax,
    setSortField,
    setSortDesc,
    fetchSessions,
    resetFilters,
  } = useSearchStore();

  const { getDiaperModel, loadDiaperModels } = useDiaperStore();
  const currentNovaAccountId = useSessionsStore((s) => s.currentNovaAccountId);
  const [metas, setMetas] = useState<Record<string, BackendMeta>>({});
  const [flagsOpen, setFlagsOpen] = useState(true);
  const [numericOpen, setNumericOpen] = useState(false);

  useEffect(() => {
    checkServer();
  }, [checkServer]);

  useEffect(() => {
    if (!currentNovaAccountId) return;
    loadDiaperModels(currentNovaAccountId).catch(console.error);
  }, [currentNovaAccountId, loadDiaperModels]);

  useEffect(() => {
    if (serverOnline) {
      loadBuckets();
    }
  }, [serverOnline, loadBuckets]);

  useEffect(() => {
    if (!serverOnline || !activeBucket) return;
    fetchSessions(activeBucket);
  }, [serverOnline, activeBucket, fetchSessions]);

  useEffect(() => {
    if (!serverOnline || sessions.length === 0) return;
    getAllClipMeta()
      .then(setMetas)
      .catch(() => setMetas({}));
  }, [serverOnline, sessions.length, loadedBucket]);

  const stats = useMemo(() => {
    let flat = 0;
    let dry = 0;
    let qw = 0;
    for (const s of sessions) {
      if (s.flatline_like) flat++;
      if (s.mostly_dry) dry++;
      if (s.quickly_high_wetness) qw++;
    }
    const n = sessions.length;
    return {
      flat,
      dry,
      qw,
      n,
      flatPct: n ? ((flat / n) * 100).toFixed(1) : '0',
      dryPct: n ? ((dry / n) * 100).toFixed(1) : '0',
      qwPct: n ? ((qw / n) * 100).toFixed(1) : '0',
    };
  }, [sessions]);

  const modelCodes = useMemo(() => {
    const set = new Set<number>();
    sessions.forEach((s) => set.add(s.model));
    return Array.from(set).sort((a, b) => a - b);
  }, [sessions]);

  const sites = useMemo(() => {
    const set = new Set<string>();
    sessions.forEach((s) => {
      if (s.site_name) set.add(s.site_name);
    });
    return Array.from(set).sort();
  }, [sessions]);

  const numericMeta = useMemo(
    () => new Map(NUMERIC_SESSION_FIELDS.map((f) => [f.key, f] as const)),
    []
  );

  const filteredSorted = useMemo(() => {
    let rows: SessionRow[] = [...sessions];

    if (modelFilter !== 'all') {
      rows = rows.filter((s) => s.model === modelFilter);
    }
    if (siteFilter !== 'all') {
      rows = rows.filter((s) => s.site_name === siteFilter);
    }

    for (const { key } of BOOLEAN_SESSION_FIELDS) {
      const k = String(key);
      const state = (booleanFilters[k] ?? 'any') as TriState;
      rows = rows.filter((s) => matchesBooleanFilter(s, k, state));
    }

    for (const { key } of NUMERIC_SESSION_FIELDS) {
      const r = numericRange[key] || { min: '', max: '' };
      rows = rows.filter((s) => matchesNumericFilter(s, key, r.min, r.max));
    }

    const q = textSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((s) => {
        const mac = (s.clip_address || '').toLowerCase();
        const stem = (s.clip_stem || s.clip || '').toLowerCase();
        const name = (metas[s.clip_address]?.clip_name || '').toLowerCase();
        return mac.includes(q) || stem.includes(q) || name.includes(q);
      });
    }

    const cq = textCriterion.trim().toLowerCase();
    if (cq) {
      rows = rows.filter((s) => {
        const cn = String(s.criterion_name ?? '').toLowerCase();
        const cs = String(s.criterion_signature ?? '').toLowerCase();
        return cn.includes(cq) || cs.includes(cq);
      });
    }

    const mul = sortDesc ? -1 : 1;
    rows.sort((a, b) => compareSessionField(a, b, sortField) * mul);

    return rows;
  }, [
    sessions,
    modelFilter,
    siteFilter,
    textSearch,
    textCriterion,
    booleanFilters,
    numericRange,
    sortField,
    sortDesc,
    metas,
  ]);

  const openInspector = (s: AnalysisSession) => {
    const clip = encodeURIComponent(s.clip_address);
    const start = encodeURIComponent(s.start_ts);
    navigate(`/diaper-inspector?clip=${clip}&diaperStart=${start}`);
  };

  const triSelect = (key: string, value: TriState) => (
    <select
      className="border rounded px-1 py-0.5 text-[11px] max-w-[100px]"
      value={value}
      onChange={(e) => setBooleanFilter(key, e.target.value as TriState)}
    >
      <option value="any">Any</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-2">🔎 Diaper Search</h2>
        <p className="text-gray-600 text-sm max-w-3xl">
          Pipeline <code className="bg-gray-100 px-1 rounded">session_samples</code> from{' '}
          <code className="bg-gray-100 px-1 rounded">diaper-accuracy1/results/&lt;period&gt;/results.json</code>.
          Filter booleans (tri-state), numeric min/max, criterion text — sort by any primitive field — open a row in the
          Inspector.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Data bucket (period)</label>
          <select
            className="border rounded px-3 py-2 text-sm min-w-[200px]"
            value={activeBucket || ''}
            onChange={(e) => switchBucket(e.target.value || null)}
            disabled={!serverOnline || loadingBuckets}
          >
            <option value="">{loadingBuckets ? 'Loading…' : 'Select bucket'}</option>
            {availableBuckets.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          disabled={!serverOnline || !activeBucket || loading}
          onClick={() => activeBucket && fetchSessions(activeBucket)}
        >
          {loading ? 'Loading…' : 'Refresh analysis'}
        </button>
        {resultsPath && (
          <span className="text-xs text-gray-400 font-mono truncate max-w-md" title={resultsPath}>
            {resultsPath}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm space-y-2">
          <p>{error}</p>
          {resultsPath && (
            <p className="font-mono text-xs break-all text-amber-800">
              <span className="font-sans font-semibold">Expected file: </span>
              {resultsPath}
            </p>
          )}
          {analysisBaseDir && (
            <p className="font-mono text-xs break-all text-amber-800">
              <span className="font-sans font-semibold">Resolved results base: </span>
              {analysisBaseDir}
            </p>
          )}
          {CLIP_MONITOR_IS_REMOTE && (
            <p className="text-xs text-amber-800">
              The app is using a <strong>remote</strong> clip server — that host must contain{' '}
              <code className="bg-amber-100 px-1 rounded">diaper-accuracy1/results</code> or set{' '}
              <code className="bg-amber-100 px-1 rounded">ANALYSIS_RESULTS_DIR</code> there.
            </p>
          )}
        </div>
      )}

      {sessions.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500 font-medium">Flatline-like</p>
              <p className="text-2xl font-bold text-orange-700">{stats.flat}</p>
              <p className="text-xs text-gray-400">{stats.flatPct}% of {stats.n}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500 font-medium">Mostly dry</p>
              <p className="text-2xl font-bold text-sky-700">{stats.dry}</p>
              <p className="text-xs text-gray-400">{stats.dryPct}% of {stats.n}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500 font-medium">Quickly high wetness</p>
              <p className="text-2xl font-bold text-violet-700">{stats.qw}</p>
              <p className="text-xs text-gray-400">{stats.qwPct}% of {stats.n}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Model</label>
              <select
                className="border rounded px-2 py-1.5 text-sm"
                value={modelFilter === 'all' ? 'all' : String(modelFilter)}
                onChange={(e) => {
                  const v = e.target.value;
                  setModelFilter(v === 'all' ? 'all' : Number(v));
                }}
              >
                <option value="all">All models</option>
                {modelCodes.map((c) => (
                  <option key={c} value={c}>
                    {getDiaperModel(c)?.name || `Model ${c}`} ({c})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Site</label>
              <select
                className="border rounded px-2 py-1.5 text-sm min-w-[160px]"
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value as string | 'all')}
              >
                <option value="all">All sites</option>
                {sites.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Search clip</label>
              <input
                type="search"
                className="border rounded px-2 py-1.5 text-sm w-full"
                placeholder="MAC, stem, clip name…"
                value={textSearch}
                onChange={(e) => setTextSearch(e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Criterion contains</label>
              <input
                type="search"
                className="border rounded px-2 py-1.5 text-sm w-full"
                placeholder="name or signature…"
                value={textCriterion}
                onChange={(e) => setTextCriterion(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Sort by</label>
              <select
                className="border rounded px-2 py-1.5 text-sm min-w-[200px]"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              >
                {SORTABLE_FIELDS.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pt-5">
              <input type="checkbox" checked={sortDesc} onChange={(e) => setSortDesc(e.target.checked)} />
              Descending
            </label>
            <button type="button" className="text-sm text-gray-600 underline pt-5" onClick={resetFilters}>
              Reset filters
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
              onClick={() => setFlagsOpen(!flagsOpen)}
            >
              <span>Boolean flags (tri-state)</span>
              <span className="text-gray-400">{flagsOpen ? '▼' : '▶'}</span>
            </button>
            {flagsOpen && (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-t border-gray-100">
                {BOOLEAN_SESSION_FIELDS.map(({ key, label }) => {
                  const k = String(key);
                  return (
                    <div key={k} className="flex items-center justify-between gap-2 border border-gray-100 rounded px-2 py-1.5">
                      <span className="text-xs text-gray-700 truncate" title={label}>
                        {label}
                      </span>
                      {triSelect(k, (booleanFilters[k] ?? 'any') as TriState)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
              onClick={() => setNumericOpen(!numericOpen)}
            >
              <span>Numeric min / max ({NUMERIC_SESSION_FIELDS.length} fields)</span>
              <span className="text-gray-400">{numericOpen ? '▼' : '▶'}</span>
            </button>
            {numericOpen && (
              <div className="p-4 max-h-[28rem] overflow-y-auto border-t border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {NUMERIC_SESSION_FIELDS.map(({ key, label }) => {
                    const r = numericRange[key] || { min: '', max: '' };
                    return (
                      <div key={key} className="flex flex-col gap-1 border border-gray-100 rounded p-2">
                        <span className="text-[11px] font-medium text-gray-600 truncate" title={label}>
                          {label}
                        </span>
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="border rounded px-1.5 py-0.5 text-xs w-full min-w-0"
                            placeholder="min"
                            value={r.min}
                            onChange={(e) => setNumericMin(key, e.target.value)}
                          />
                          <input
                            type="text"
                            inputMode="decimal"
                            className="border rounded px-1.5 py-0.5 text-xs w-full min-w-0"
                            placeholder="max"
                            value={r.max}
                            onChange={(e) => setNumericMax(key, e.target.value)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600">
            Showing <strong>{filteredSorted.length}</strong> of <strong>{sessions.length}</strong> sessions
            {loadedBucket && (
              <>
                {' '}
                · bucket <code className="bg-gray-100 px-1 rounded text-xs">{loadedBucket}</code>
              </>
            )}
          </p>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full text-[11px] text-left border-collapse">
                <thead className="text-gray-700 bg-gray-50 sticky top-0 z-20 shadow-sm">
                  <tr>
                    <th
                      className="px-2 py-1.5 border-b border-gray-200 sticky left-0 bg-gray-50 z-30 min-w-[140px] max-w-[200px]"
                      title="Clip"
                    >
                      Clip
                    </th>
                    {BOOLEAN_SESSION_FIELDS.map(({ key, label }) => (
                      <th
                        key={String(key)}
                        className="px-1 py-1.5 border-b border-gray-200 whitespace-nowrap text-center"
                        title={label}
                      >
                        {BOOL_HEADER_SHORT[String(key)] ?? String(key).slice(0, 4)}
                      </th>
                    ))}
                    {STRING_SESSION_FIELDS.map(({ key, label }) => (
                      <th key={key} className="px-1.5 py-1.5 border-b border-gray-200 whitespace-nowrap" title={label}>
                        {label}
                      </th>
                    ))}
                    <th className="px-1.5 py-1.5 border-b border-gray-200 whitespace-nowrap">start_ts</th>
                    <th className="px-1.5 py-1.5 border-b border-gray-200 whitespace-nowrap">end_ts</th>
                    {NUMERIC_SESSION_FIELDS.map(({ key, label }) => (
                      <th key={key} className="px-1 py-1.5 border-b border-gray-200 whitespace-nowrap" title={label}>
                        {label}
                      </th>
                    ))}
                    {OBJECT_SESSION_FIELDS.map(({ key, label }) => (
                      <th key={key} className="px-1 py-1.5 border-b border-gray-200 min-w-[120px]" title={label}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((s, i) => {
                    const name = metas[s.clip_address]?.clip_name;
                    const label = name || s.clip_address?.slice(-17) || s.clip;
                    return (
                      <tr
                        key={`${s.clip_address}-${s.counter}-${s.start_ts}-${i}`}
                        className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer"
                        onClick={() => openInspector(s)}
                      >
                        <td
                          className="px-2 py-1 sticky left-0 bg-white z-10 font-mono max-w-[200px] truncate border-r border-gray-100"
                          title={s.clip_address}
                        >
                          {label}
                        </td>
                        {BOOLEAN_SESSION_FIELDS.map(({ key }) => (
                          <td key={String(key)} className="px-0.5 py-1 text-center font-mono">
                            {formatCell(s[key as keyof SessionRow])}
                          </td>
                        ))}
                        {STRING_SESSION_FIELDS.map(({ key }) => (
                          <td key={key} className="px-1 py-1 max-w-[120px] truncate" title={String(s[key] ?? '')}>
                            {formatCell(s[key])}
                          </td>
                        ))}
                        <td className="px-1 py-1 whitespace-nowrap">
                          {s.start_ts ? format(parseISO(s.start_ts), 'MMM d HH:mm') : '—'}
                        </td>
                        <td className="px-1 py-1 whitespace-nowrap">
                          {s.end_ts ? format(parseISO(s.end_ts), 'MMM d HH:mm') : '—'}
                        </td>
                        {NUMERIC_SESSION_FIELDS.map(({ key, decimals }) => {
                          const v = s[key];
                          const d = decimals ?? numericMeta.get(key)?.decimals;
                          return (
                            <td key={key} className="px-1 py-1 whitespace-nowrap text-right font-mono">
                              {key === 'duration_hours' ? formatDurationHours(v as number | undefined) : formatCell(v, d)}
                            </td>
                          );
                        })}
                        {OBJECT_SESSION_FIELDS.map(({ key }) => {
                          const raw = s[key];
                          const full =
                            raw && typeof raw === 'object' ? JSON.stringify(raw, null, 2) : String(raw ?? '—');
                          return (
                            <td key={key} className="px-1 py-1 font-mono align-top max-w-[180px]" title={full}>
                              <span className="line-clamp-2">{formatJsonPreview(raw)}</span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredSorted.length === 0 && (
              <p className="p-8 text-center text-gray-500">No rows match the current filters.</p>
            )}
          </div>
        </>
      )}

      {!loading && !error && activeBucket && sessions.length === 0 && (
        <div className="bg-white rounded-lg p-12 text-center text-gray-600">
          <p>No analysis sessions loaded. Run the pipeline for this period so </p>
          <code className="text-xs bg-gray-100 px-1">results.json</code> exists under{' '}
          <code className="text-xs bg-gray-100 px-1">diaper-accuracy1/results/{activeBucket}/</code>
        </div>
      )}
    </div>
  );
};
